import { useCallback, useMemo } from 'react';
import { isEqual } from 'underscore';

import useLoadCategories from '@/connection/hooks/use-load-categories';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { saveWorkingProjectSessionByField } from '@/store/slices/project';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import CategoriesSelect from './categories-select';

type EditSessionCategoriesProps = {
  sessionUuid?: string | null;
};

const EditSessionCategories = ({ sessionUuid }: EditSessionCategoriesProps) => {
  useLoadCategories();

  const workingProject = useAppSelector(getWorkingProject);
  const session = useMemo(() => {
    if (sessionUuid) {
      return workingProject?.sessions?.find((s) => s.uuid === sessionUuid);
    }
    return workingProject?.sessions?.[0];
  }, [sessionUuid, workingProject?.sessions]);

  const dispatch = useAppDispatch();

  const defaultCategories = useMemo(() => {
    if (!session?.tracks) return [];

    return session?.tracks
      .map((c) => {
        return {
          label: `${c}`.trim(),
          value: c,
          meta: c,
        };
      })
      .filter(Boolean) as TReactSelectOption<string, string, string>[];
  }, [session]);

  const handleMenuClose = useCallback(
    (selectedCategories: TReactSelectOption[]) => {
      if (!session) return;

      // return early if no change
      const newCategories = [...(selectedCategories || [])].map((c) => c.value).sort();
      const oldCategories = [...(session.tracks || [])].sort();
      if (isEqual(newCategories, oldCategories)) {
        return;
      }

      dispatch(
        saveWorkingProjectSessionByField({
          uuid: session.uuid,
          tracks: selectedCategories.map((c) => c.value.toString()).filter(Boolean),
        })
      );
    },
    [dispatch, session]
  );

  return <CategoriesSelect defaultCategories={defaultCategories} onMenuClose={handleMenuClose} />;
};

export default EditSessionCategories;
